import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Phone, Car, Shield, FileText, Clock, MapPin, ChevronLeft, Star, Send } from "lucide-react";
import { useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_content-update-22/artifacts/mejz8dgl_file_00000000c3bc622f96fe678a8b87cead.png";

const coverageAreas = ["Ipswich", "Toowoomba", "Lockyer Valley"];

const services = [
  {
    icon: Car,
    title: "Accident Replacement Vehicle",
    description: "Get back on the road quickly with a free replacement vehicle while yours is being repaired."
  },
  {
    icon: Shield,
    title: "Personal Injury Claim Assistance",
    description: "Connect with trusted partners who can help you navigate personal injury claims after an accident."
  },
  {
    icon: FileText,
    title: "Insurance Claim Guidance",
    description: "Expert guidance to help you understand and submit your insurance claim correctly."
  },
  {
    icon: Clock,
    title: "24/7 Availability",
    description: "We're here for you around the clock. Call anytime, day or night, and we'll help."
  }
];

const testimonials = [
  {
    name: "Sarah M.",
    location: "Ipswich",
    rating: 5,
    text: "After my accident, I was completely overwhelmed. My Roadside Hero took care of everything - got me a replacement car the same day and helped me understand my insurance options. Incredible service!"
  },
  {
    name: "James T.",
    location: "Toowoomba",
    rating: 5,
    text: "Professional, fast, and genuinely caring. They went above and beyond to help me get home safely after my breakdown. Can't recommend them enough!"
  },
  {
    name: "Michelle R.",
    location: "Lockyer Valley",
    rating: 5,
    text: "I didn't know who to call after my accident. My Roadside Hero was a lifesaver - completely free service and they connected me with people who actually helped. Thank you!"
  }
];

const BrisbaneRegion = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        region: "Brisbane"
      });
      setSubmitStatus("success");
      setFormData({ name: "", phone: "", email: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="flex items-center gap-2 text-[hsl(0,0%,8%)] hover:text-[hsl(355,85%,45%)] transition-colors"
                data-testid="back-to-home"
              >
                <ChevronLeft className="h-5 w-5" />
                <span className="font-medium hidden sm:inline">Back</span>
              </Link>
              <img src={LOGO_URL} alt="My Roadside Hero" className="h-10 w-auto" data-testid="nav-logo" />
            </div>
            <a 
              href="tel:1300753727"
              className="flex items-center gap-2 bg-[hsl(355,85%,45%)] text-white px-4 py-2 rounded-full font-bold text-lg hover:bg-[hsl(355,85%,38%)] transition-all hover:scale-105 shadow-md"
              data-testid="nav-phone-button"
            >
              <Phone className="h-5 w-5" />
              <span className="hidden sm:inline">1300 753 727</span>
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_content-update-22/artifacts/9wuqqzrl_Old_Bremer_Tafe_-_Ipswich-1300.png"
            alt="Brisbane Region"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(0,0%,8%)] via-[hsl(0,0%,8%)]/80 to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl"
          >
            <span className="inline-block bg-[hsl(355,85%,45%)] text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
              Brisbane Region
            </span>
            <h1 className="font-heading text-5xl md:text-7xl font-black text-white uppercase tracking-tight mb-6" data-testid="brisbane-hero-title">
              Your Roadside Hero in Brisbane
            </h1>
            <p className="text-xl text-slate-200 leading-relaxed mb-8">
              Been in an accident? We're a <span className="text-[hsl(355,85%,45%)] font-semibold">100% free, independent assistance service</span> dedicated to helping you get back on track.
            </p>
            <div className="flex flex-wrap gap-4">
              <a 
                href="tel:1300753727"
                className="inline-flex items-center gap-2 bg-[hsl(355,85%,45%)] text-white px-8 py-4 rounded-full font-bold text-lg uppercase tracking-wider hover:bg-[hsl(355,85%,38%)] transition-all hover:scale-105 shadow-lg"
                data-testid="hero-call-button"
              >
                <Phone className="h-5 w-5" />
                Call Now - 1300 753 727
              </a>
              <a 
                href="#contact"
                className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-bold text-lg uppercase tracking-wider hover:bg-white/10 transition-all"
                data-testid="hero-contact-button"
              >
                Contact Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Coverage Areas */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[hsl(0,0%,8%)] uppercase mb-4">
              Areas We Cover
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We provide roadside assistance throughout the Brisbane region including:
            </p>
          </motion.div>
          <div className="flex flex-wrap justify-center gap-4">
            {coverageAreas.map((area, index) => (
              <motion.div
                key={area}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="flex items-center gap-2 bg-white px-6 py-3 rounded-full shadow-md border border-slate-200"
                data-testid={`coverage-area-${area.toLowerCase().replace(' ', '-')}`}
              >
                <MapPin className="h-5 w-5 text-[hsl(355,85%,45%)]" />
                <span className="font-semibold text-[hsl(0,0%,8%)]">{area}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[hsl(0,0%,8%)] uppercase mb-4">
              How We Help You
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We provide clear, unbiased information and connect you with trusted partners
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm hover:shadow-lg hover:border-[hsl(355,85%,45%)]/50 transition-all duration-300 group"
                data-testid={`service-card-${index}`}
              >
                <div className="bg-slate-50 group-hover:bg-[hsl(355,85%,45%)] w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-colors">
                  <service.icon className="h-7 w-7 text-[hsl(355,85%,45%)] group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading text-xl font-bold text-[hsl(0,0%,8%)] mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[hsl(0,0%,8%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid lg:grid-cols-2 gap-12 items-center"
          >
            <div>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-white uppercase mb-6">
                Why Choose My Roadside Hero?
              </h2>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="bg-[hsl(355,85%,45%)] w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">100% Free Service</h3>
                    <p className="text-slate-300">No hidden costs. Our assistance service is completely free to you.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-[hsl(355,85%,45%)] w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">Independent & Unbiased</h3>
                    <p className="text-slate-300">We work for you - not insurers, police, or government.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-[hsl(355,85%,45%)] w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">Industry Experts</h3>
                    <p className="text-slate-300">Founded by industry insiders with years of insurance and claims experience.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="bg-[hsl(355,85%,45%)] w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg mb-1">We Help the Uninsured Too</h3>
                    <p className="text-slate-300">Don't have insurance? We can still help you navigate your options.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_content-update-22/artifacts/xorwwktz_Untitled-design-1024x576.png"
                alt="Car Accident Assistance"
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[hsl(355,85%,45%)] text-white p-6 rounded-xl shadow-lg">
                <div className="font-heading text-4xl font-black">24/7</div>
                <div className="text-sm uppercase tracking-wider">Available</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-[hsl(0,0%,8%)] uppercase mb-4">
              What Our Clients Say
            </h2>
            <p className="text-slate-600 text-lg">Real stories from people we've helped</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md border border-slate-200"
                data-testid={`testimonial-${index}`}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-[hsl(355,85%,45%)] text-[hsl(355,85%,45%)]" />
                  ))}
                </div>
                <p className="text-slate-600 mb-4 leading-relaxed">"{testimonial.text}"</p>
                <div className="border-t border-slate-100 pt-4">
                  <p className="font-bold text-[hsl(0,0%,8%)]">{testimonial.name}</p>
                  <p className="text-sm text-slate-500">{testimonial.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section id="contact" className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-[hsl(0,0%,8%)] uppercase mb-6">
                Get In Touch
              </h2>
              <p className="text-slate-600 text-lg mb-8 leading-relaxed">
                Need help after an accident? Fill out the form and our team will get back to you as soon as possible. Or call us directly at <a href="tel:1300753727" className="text-[hsl(355,85%,45%)] font-semibold">1300 753 727</a>.
              </p>
              <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h3 className="font-heading text-xl font-bold text-[hsl(0,0%,8%)] mb-4">Quick Contact</h3>
                <div className="space-y-3">
                  <a 
                    href="tel:1300753727"
                    className="flex items-center gap-3 text-slate-600 hover:text-[hsl(355,85%,45%)] transition-colors"
                  >
                    <Phone className="h-5 w-5 text-[hsl(355,85%,45%)]" />
                    1300 753 727
                  </a>
                  <div className="flex items-center gap-3 text-slate-600">
                    <Clock className="h-5 w-5 text-[hsl(355,85%,45%)]" />
                    Available 24/7
                  </div>
                  <div className="flex items-center gap-3 text-slate-600">
                    <MapPin className="h-5 w-5 text-[hsl(355,85%,45%)]" />
                    Servicing Brisbane Region
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <form onSubmit={handleSubmit} className="bg-white rounded-xl p-8 shadow-lg border border-slate-200" data-testid="contact-form">
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-semibold text-[hsl(0,0%,8%)] mb-2">Name</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-lg focus:border-[hsl(355,85%,45%)] focus:ring-2 focus:ring-[hsl(355,85%,45%)]/20 outline-none transition-all"
                      placeholder="Your name"
                      required
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[hsl(0,0%,8%)] mb-2">Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-lg focus:border-[hsl(355,85%,45%)] focus:ring-2 focus:ring-[hsl(355,85%,45%)]/20 outline-none transition-all"
                      placeholder="Your phone number"
                      required
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[hsl(0,0%,8%)] mb-2">Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-lg focus:border-[hsl(355,85%,45%)] focus:ring-2 focus:ring-[hsl(355,85%,45%)]/20 outline-none transition-all"
                      placeholder="Your email"
                      required
                      data-testid="contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[hsl(0,0%,8%)] mb-2">Message</label>
                    <textarea
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="w-full h-32 px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg focus:border-[hsl(355,85%,45%)] focus:ring-2 focus:ring-[hsl(355,85%,45%)]/20 outline-none transition-all resize-none"
                      placeholder="How can we help you?"
                      required
                      data-testid="contact-message"
                    />
                  </div>
                  {submitStatus === "success" && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg" data-testid="contact-success">
                      Thank you! We'll be in touch soon.
                    </div>
                  )}
                  {submitStatus === "error" && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg" data-testid="contact-error">
                      Something went wrong. Please call us directly at 1300 753 727.
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full h-14 bg-[hsl(355,85%,45%)] text-white rounded-full font-bold uppercase tracking-wider hover:bg-[hsl(355,85%,38%)] transition-all hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      "Sending..."
                    ) : (
                      <>
                        <Send className="h-5 w-5" />
                        Send Message
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[hsl(0,0%,8%)] text-white py-12 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <img src={LOGO_URL} alt="My Roadside Hero" className="h-12 w-auto" />
            <div className="text-center md:text-left">
              <p className="text-slate-300">
                Available 24/7 - Call us anytime at{" "}
                <a href="tel:1300753727" className="text-[hsl(355,85%,45%)] font-semibold hover:underline">
                  1300 753 727
                </a>
              </p>
              <p className="text-slate-400 text-sm mt-2">
                Servicing Ipswich, Toowoomba, and Lockyer Valley
              </p>
            </div>
            <Link 
              to="/"
              className="text-slate-300 hover:text-white transition-colors"
              data-testid="footer-change-region"
            >
              Change Region
            </Link>
          </div>
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400 text-sm">
            &copy; {new Date().getFullYear()} My Roadside Hero. All rights reserved.
            {" | "}
            <a href="https://admin-referral-hub.emergent.host/admin/login" className="hover:text-slate-300 transition-colors" data-testid="admin-link">
              Admin
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BrisbaneRegion;
